

























import {
  computed,
  defineComponent,
  ref,
  provide,
  watch,
  toRef,
  onBeforeUnmount,
  onMounted,
  PropType,
} from "@vue/composition-api";
import { ContentFile, InputContentFileCreate } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import { ContentSet, InputContentSetUpdate } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { VaultFile } from "@scrile/api-provider/dist/api/VaultsProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UploadFileData } from "@scrile/tools/dist/lib/FileUploader";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { getFileMimeType } from "@/lib/fileHelpers";
import FileUploader, { showDefaultUploadError } from "@/lib/FileUploader";
import { PostFileData } from "@/types";
import { EVENTS, MIN_PHOTO_GALLERY_FILES } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import ModalView from "@/components/ModalView";
import CreatePostView from "../CreatePostView";

interface FileUploadData {
  id: string;
  file: File;
  uploadProgress: number;
}

function isFileUploadData(data: any): data is FileUploadData {
  return typeof data.file !== "undefined";
}

export default defineComponent({
  name: "CreatePostController",
  components: { CreatePostView, ModalView, BaseConfirm, BaseButton },
  inject: [],
  props: {
    postId: { type: String, required: true },
    type: { type: String as PropType<ContentFeedTypeEnum>, required: true },
    show: { type: Boolean, required: true },
    userId: { type: String, required: true },
  },
  setup(props, { emit }) {
    const loading = ref(false);
    const saving = ref<Promise<any> | null>(null);
    const savingDraft = ref(false);
    const postData = ref<ContentSet | null>(null);
    const postFiles = ref<(ContentFile | FileUploadData)[]>([]);
    const fileDeleteQueue = ref<Function[]>([]);
    const showConfirm = ref(false);
    const oneTimePackageId = ref<string | null>(null);
    const subscriptionLevelId = ref<string | null>(null);

    const postFileDataList = computed<PostFileData[]>(() => {
      return postFiles.value.map(i => {
        if (isFileUploadData(i)) {
          return {
            id: i.id,
            mediaType: getFileMimeType(i.file),
            fileName: i.file.name,
            size: i.file.size,
            public: false,
            uploading: true,
            duration: null,
            url: "",
            uploadProgress: i.uploadProgress,
            processingStatus: "NEW",
          };
        } else {
          return {
            id: i.id,
            mediaType: i.mediaType,
            fileName: i.title,
            size: i.size,
            public: i.publicAccess,
            uploading: false,
            duration: null,
            url: i.urlPart,
            uploadProgress: 100,
            processingStatus: i.processingStatus,
          };
        }
      });
    });

    const requestAndSetPostData = async (postId: string) => {
      const response = await providers.ContentSetProvider.search({
        data: {
          userId: props.userId,
          ids: [postId],
        },
      });
      postData.value = response.result.find(i => i.id === postId) || null;
      emit("updatePostData", postData.value);
    };

    const requestAndSetPostFiles = async (postId: string) => {
      const response = await providers.ContentFilesProvider.search({
        data: {
          setId: [postId],
          size: 9999,
          sort: "SORT,ASC",
        },
      });
      postFiles.value = response.result;
    };

    const watchPostId = async (postId: string) => {
      if (!postData.value && postId) {
        try {
          loading.value = true;
          await Promise.all([requestAndSetPostData(postId), requestAndSetPostFiles(postId)]);
        } finally {
          loading.value = false;
        }
      }
    };
    watch(toRef(props, "postId"), (postId: string) => watchPostId(postId), { immediate: true });

    const createPost = async (type: ContentFeedTypeEnum) => {
      try {
        loading.value = true;
        postData.value = await providers.ContentSetProvider.create({
          data: {
            type,
            userId: props.userId,
          },
        });
        emit("updatePostData", postData.value);
      } finally {
        loading.value = false;
      }
    };

    const getPostData = async () => {
      return postData.value || (await new Promise<ContentSet>(r => emit("updatePostData", (p: any) => r(p))));
    };

    const onFileRemove = async (fileId: string, { isEditing = false }: { isEditing?: boolean } = {}) => {
      const file = postFiles.value.find(f => f.id === fileId);
      if (!file) return;
      postFiles.value = postFiles.value.filter(i => i.id !== fileId);

      if (isFileUploadData(file)) {
        FileUploader.removeFromQueue(fileId);
      } else if (isEditing) {
        const post = await getPostData();
        fileDeleteQueue.value.push(() => providers.ContentFilesProvider.delete({ setId: post.id, id: fileId }));
      } else {
        const post = await getPostData();
        await providers.ContentFilesProvider.delete({ setId: post.id, id: fileId });
      }
    };

    const reset = () => {
      emit("close");
      if (!!postData.value) {
        emitter.emit(EVENTS.CONTENT_FEED_UPDATED, { userId: postData.value.userId, feedId: postData.value.id });
      }
      postData.value = null;
      postFiles.value.forEach(i => {
        if (isFileUploadData(i)) {
          onFileRemove(i.id);
        }
      });
      postFiles.value.length > 0 && (postFiles.value = []);
    };

    const waitFileUpload: () => Promise<void> = async () => {
      if (postFileDataList.value.find(i => i.uploading)) {
        await new Promise(r => setTimeout(r, 500));
        return waitFileUpload();
      }
    };

    const updateGalleryCoverPublicFiles = async () => {
      if (!oneTimePackageId.value || oneTimePackageId.value === "0") return;
      const publicAccessFile = postFiles.value.find(f => !isFileUploadData(f) && f.publicAccess);
      if (!!publicAccessFile) return;

      let promises: Promise<any>[] = [];
      const coverFiles = postFiles.value.filter(f => !isFileUploadData(f)).slice(0, MIN_PHOTO_GALLERY_FILES);
      coverFiles.forEach(coverFile => {
        if (!postData.value) return;
        promises.push(
          providers.ContentFilesProvider.update({
            setId: postData.value.id,
            data: { id: coverFile.id, publicAccess: true },
          })
        );
      });
      await Promise.all(promises);
    };

    const setPostFileSorting = async (postId: string) => {
      if (!postFiles.value.find(i => isFileUploadData(i))) {
        for (const [index, fileData] of postFiles.value.entries()) {
          await providers.ContentFilesProvider.update({
            setId: postId,
            data: {
              id: fileData.id,
              sort: index,
            },
          });
        }
      }
    };

    const onUpdate = async (
      { data, closeOnSave }: { data: Omit<InputContentSetUpdate, "id">; closeOnSave?: boolean },
      cb?: Function
    ) => {
      const post = await getPostData();
      let deferred: Function = () => {};
      try {
        saving.value = new Promise<any>(r => {
          deferred = r;
        });
        if (post.type === ContentFeedTypeEnum.PHOTO_GALLERY) {
          await updateGalleryCoverPublicFiles();
        }
        if (data.published) {
          await Promise.all([
            ...fileDeleteQueue.value.map(promise => promise()),
            waitFileUpload(),
            setPostFileSorting(post.id),
          ]);
        }
        postData.value = await providers.ContentSetProvider.update({
          data: {
            ...data,
            id: post.id,
          },
        });
        emit("updatePostData", postData.value);
        if (closeOnSave) {
          reset();
        }
      } finally {
        cb && cb();
        deferred();
        saving.value = null;
      }
    };

    const updatePostData = (newPostData: ContentSet) => {
      postData.value = newPostData;
    };

    const onFileUploaded = async ({
      id,
      uuid,
      file,
      post,
      publicAccess,
    }: {
      id: string;
      uuid: string;
      file: File;
      post: ContentSet;
      publicAccess: boolean;
    }) => {
      const inputFileData: InputContentFileCreate = {
        title: file.name,
        name: file.name,
        uuid,
        publicAccess,
      };
      const contentFile = await providers.ContentFilesProvider.create({ setId: post.id, data: inputFileData });
      postFiles.value = postFiles.value.map(i => {
        return i.id === id ? contentFile : i;
      });
      await setPostFileSorting(post.id);
    };

    const onAddFiles = async (
      files: FileList,
      { unshift = false, publicAccess = false }: { unshift?: boolean; publicAccess?: boolean } = {}
    ) => {
      const post = await getPostData();
      const uploadUrls = await providers.ContentFilesProvider.getUploadUrls({
        setId: post.id,
        count: files.length,
      });
      const uploadData: UploadFileData[] = [];
      Array.from(files).forEach((file, index) => {
        const uuid = uploadUrls[index].uuid;
        const id = "file__" + uuid;
        postFiles.value[unshift ? "unshift" : "push"]({ id, file, uploadProgress: 0 });
        let uploadErrorCount = 0;
        const data: UploadFileData = {
          urlPart: uploadUrls[index].urlPart,
          fileType: getFileMimeType(file),
          file,
          id,
          onSuccess: () => onFileUploaded({ id, post, file, uuid, publicAccess }),
          onUploadProgress: p => {
            postFiles.value.find((i, index) => {
              if (i.id === id && isFileUploadData(i)) {
                postFiles.value[index] = {
                  ...i,
                  uploadProgress: Math.floor(((p?.loaded ?? 0) / (p?.total ?? 0)) * 100),
                };
                return true;
              }
              return false;
            });
          },
          onError: () => {
            if (!postFiles.value.some(i => i.id === id)) {
              return;
            }
            uploadErrorCount++;
            if (uploadErrorCount < 5) {
              setTimeout(() => FileUploader.addToQueue([data]), 5000);
            } else {
              showDefaultUploadError();
              onFileRemove(data.id);
            }
          },
        };
        uploadData.push(data);
      });
      FileUploader.addToQueue(uploadData);
    };

    const onAddFilesFromVault = async (files: VaultFile[]) => {
      const post = await getPostData();
      for (const file of files) {
        const resultFile = await providers.ContentFilesProvider.create({
          setId: post.id,
          data: {
            name: file.uuid,
            uuid: file.uuid,
            publicAccess: false,
            title: file.uuid,
          },
        });
        postFiles.value.push(resultFile);
      }
    };

    const onDeletePost = async () => {
      showConfirm.value = false;
      await saving.value;
      if (!postData.value) return;
      providers.ContentSetProvider.delete({ id: postData.value.id });
      reset();
    };

    const onDraftSave = () => {
      savingDraft.value = true;
      return onUpdate({ data: { published: false }, closeOnSave: true }, () => (savingDraft.value = false));
    };

    const onBack = async () => {
      await saving.value;
      if (!postData.value || postData.value.published) {
        reset();
      } else {
        showConfirm.value = true;
      }
    };

    const onCloseModal = async (force: boolean = false) => {
      await saving.value;
      fileDeleteQueue.value = [];
      if (postData.value && !postData.value.published) {
        if (force) {
          await onDraftSave();
        } else {
          showConfirm.value = true;
        }
        return;
      }
      reset();
    };

    const onPreviewChange = (contentFiles: ContentFile[]) => {
      const changedFilesMap: Map<string, ContentFile> = new Map();
      contentFiles.forEach(i => changedFilesMap.set(i.id, i));
      postFiles.value = postFiles.value.map(i => changedFilesMap.get(i.id) ?? i);
    };

    const onPriceChanged = ({
      recurringLevel,
      oneTimePackage,
    }: {
      recurringLevel: SubscriptionLevel | null;
      oneTimePackage: PaymentPackage | null;
    }) => {
      oneTimePackageId.value = oneTimePackage?.id || null;
      subscriptionLevelId.value = recurringLevel?.id || null;
    };

    onMounted(() => {
      if (!!props.postId) {
        watchPostId(props.postId);
      } else {
        createPost(props.type);
      }
      emitter.on<any>(EVENTS.CONTENT_FEED_PRICE_CHANGED, onPriceChanged);
      emitter.on<any>(EVENTS.CONTENT_FEED_PREVIEW_CHANGED, onPreviewChange);
    });

    onBeforeUnmount(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_PRICE_CHANGED, onPriceChanged);
      emitter.off<any>(EVENTS.CONTENT_FEED_PREVIEW_CHANGED, onPreviewChange);
    });

    provide("contentFeedUserId", toRef(props, "userId"));
    provide("fileList", postFileDataList);
    provide("updatePostData", updatePostData);
    provide("postAddFiles", onAddFiles);

    return {
      loading,
      showConfirm,
      savingDraft,
      postFiles,
      postData,
      postFileDataList,
      onFileRemove,
      onAddFiles,
      onAddFilesFromVault,
      onUpdate,
      onCloseModal,
      onDraftSave,
      onDeletePost,
      onBack,
    };
  },
});
